import { useEffect, useState } from 'react'
import { usePaginatedFeedback } from "./Hooks/usePaginatedFeedback"
import { formatDateShort } from './Functions/dates.js';

export default function FeedbackPage({ authUser }) {
    // STATE
    const [include, setInclude] = useState(false); // drives startAt/startAfter inside usePaginatedTitles()
    const [firstItemStack, setFirstItemStack] = useState([]); // maintain each page's first item on a stack as we go fowards, to allow going back easily
    const [referenceItem, setReferenceItem] = useState(null); // going forwards we start after the current last visible item ; going backwards we start at the previous page's first item on the stack
    const [previousDisabled, setPreviousDisabled] = useState(true);
    const [nextDisabled, setNextDisabled] = useState(true);
    const resultsPerPage = 10;

    // HOOKS
    const titles = usePaginatedFeedback(referenceItem, include, resultsPerPage);

    // SETUP
    const currentLastVisibleItem = (titles !== undefined) ? titles.docs[titles.size - 1] : null;

    useEffect(() => {
        // no back button if stack has nothing to go back to
        if (firstItemStack.length > 0) {
            setPreviousDisabled(false);
        } else {
            setPreviousDisabled(true);
        }

        // no forward button if number of items % rpp > 0
        if (titles !== undefined && titles.size % resultsPerPage === 0) {
            setNextDisabled(false);
        } else if (titles !== undefined) {
            setNextDisabled(true);
        }
    }, [firstItemStack, titles]);

    // PAGINATION HELPERS

    const nextPage = () => {
        setFirstItemStack([...firstItemStack, titles.docs[0]]);
        setInclude(false);
        setReferenceItem(currentLastVisibleItem);
    }

    const previousPage = () => {
        var tempArray = [...firstItemStack]; // copy by value!
        tempArray.pop();
        setFirstItemStack(tempArray);

        setInclude(true);
        setReferenceItem(firstItemStack[firstItemStack.length - 1]);
    }

    const firstPage = () => {
        if (!firstItemStack.length > 1) {
            return
        }

        // go to first
        setInclude(true);
        setReferenceItem(firstItemStack[0]);

        // clear array
        var tempArray = [];
        setFirstItemStack(tempArray);
    }

    return (
        <>
            {/* No titles; fallback, shouldn't ever happen */}
            {titles.docs.length === 0 && <div className="mt-6 relative block w-full border-2 border-gray-300 border-dashed rounded-lg p-12 text-center focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                <div className="text-center">
                    <p className="text-lg leading-6 font-medium text-gray-900">No feedback yet</p>
                    <p className="mt-2 text-sm text-gray-500">
                        <p>No feedbacj found; they will appear here as they are received.</p>
                    </p>
                </div>
            </div>}

            {/* Titles list */}
            {titles.docs.length > 0 && <>
                {/* start A */}
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div className="px-4 py-5 sm:px-6">
                        <h1 className="my-0 text-2xl font-bold text-gray-900 sm:text-3xl">Feedback List</h1>

                        {/*<p className="mt-1 max-w-2xl text-sm text-gray-500">Recently loaded titles are shown below:</p>*/}
                        {/*formLoading === true && <LoadingBanner />*/}
                        {/*errorBannerOpen === true && <ErrorBanner message={lastErrorMessage} />*/}
                    </div>

                    {titles.size > 0 && <div>
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">App version</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">OS</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Region</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Language</th>
                                </tr>
                            </thead>
                            <tbody>
                                {titles.docs.map((title, itemIdx) => (
                                    <>
                                        <tr key={`r1_${title.id}`} className={itemIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="px-2 py-4 text-sm text-gray-900">
                                                {title.data().created !== undefined && formatDateShort(title.data().created.toDate())}
                                                {!title.data().created && "——"}
                                            </td>
                                            <td className="px-2 py-4 text-sm text-gray-900">
                                                {title.data().appversion !== undefined && title.data().appversion}
                                                {!title.data().appversion && "——"}
                                            </td>
                                            <td className="px-2 py-4 text-sm text-gray-900">
                                                {title.data().model !== undefined && title.data().model}
                                                {!title.data().model && "——"}
                                            </td>
                                            <td className="px-2 py-4 text-sm text-gray-900">
                                                {(title.data().platform && title.data().osversion) && title.data().platform}&nbsp;
                                                {(title.data().platform && title.data().osversion) && title.data().osversion}
                                                {(!title.data().platform || !title.data().osversion) && "——"}
                                            </td>
                                            <td className="px-2 py-4 text-sm text-gray-900">
                                                {title.data().region !== undefined && title.data().region}
                                                {!title.data().region && title.data().region}
                                            </td>
                                            <td className="px-2 py-4 text-sm text-gray-900">
                                                {title.data().language !== undefined && title.data().language}
                                                {!title.data().language && title.data().language}
                                            </td>
                                        </tr>
                                        <tr key={`r2_${title.id}`} className={itemIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td colSpan={2} className="px-2 py-4 text-sm text-gray-900">
                                                {title.data().email && <span className='font-semibold'>{title.data().email} :</span>}
                                                {!title.data().email && "—"}
                                            </td>
                                            <td colSpan={4} className="px-2 py-4 text-sm text-gray-900">
                                                {title.data().comments && <span className='font-semibold'>{title.data().comments}</span>}
                                                {!title.data().comments && "—"}
                                            </td>
                                        </tr>
                                    </>
                                ))}
                            </tbody>
                        </table>
                    </div>}
                </div>
                {/* end A */}

                {/* Button actions */}
                <nav className="mt-6 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6" aria-label="Pagination">
                    <div className="hidden sm:block">
                        <p className="text-sm text-gray-700">
                            Showing <span className="font-medium">{(firstItemStack.length * resultsPerPage) + 1}</span> to <span className="font-medium">{firstItemStack.length === 0 ? titles.docs.length : (firstItemStack.length * resultsPerPage) + resultsPerPage}</span>
                        </p>
                    </div>
                    <div className="flex-1 flex justify-between sm:justify-end">
                        {firstItemStack.length > 1 &&
                            <button type='button' onClick={firstPage} disabled={previousDisabled} className="disabled:opacity-50 mr-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">First</button>
                        }

                        <button type='button' onClick={previousPage} disabled={previousDisabled} className="disabled:opacity-50 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">Previous</button>
                        <button type='button' onClick={nextPage} disabled={nextDisabled} className="disabled:opacity-50 ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">Next</button>
                    </div>
                </nav>
            </>}
        </>
    )
}